import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { startAuthentication } from '@simplewebauthn/browser';
import { AppService } from '../../../app.service';
import { AuthService } from '../../../auth/auth.service';
import { DeviceInfoService } from '../../services/device-info.service';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { ToasterService } from '../../services/toaster.service';
import { Capacitor } from '@capacitor/core';
@Component({
  selector: 'gtapp-user-login',
  standalone: false,
  templateUrl: './user-login.component.html',
  styleUrl: './user-login.component.scss',
})
export class UserLoginComponent implements OnInit, OnDestroy {
  @Input() loginEmail: any;
  @Input() blackListedEmails: any = [];

  @Output() emitData = new EventEmitter();

  loginForm: UntypedFormGroup = new UntypedFormGroup({
    username: new UntypedFormControl(null, Validators.required),
    auth_code: new UntypedFormControl(null),
    remember_login: new UntypedFormControl(true),
    password: new UntypedFormControl(null),
  });

  hideSignInLInkForgotPasswordBtn: boolean = false;
  isPasswordAlreadyset: boolean = false;
  fidoRegistered: any;

  //the below value will change depending on the response of the 'verify_login' api.
  showForm = 'userNameForm';
  previouslyEnteredEmail: string = '';

  showPassword: boolean = false;

  constructor(
    public router: Router,
    private spinnerService: LoadingSpinnerService,
    private toasterService: ToasterService,
    private authService: AuthService,
    private deviceInfoService: DeviceInfoService,
  ) {}
  ngOnDestroy(): void {}
  ngOnInit(): void {
    if (this.loginEmail) {
      this.loginForm.controls['username'].setValue(this.loginEmail);
      this.checkUserName();
    }
  }

  checkIfEmailChanged() {
    if (this.previouslyEnteredEmail !== this.loginForm.value?.username) {
      this.previouslyEnteredEmail = this.loginForm.value?.username;
      this.loginForm.controls['password'].clearValidators();
      this.loginForm.controls['password'].updateValueAndValidity();
      this.loginForm.controls['auth_code'].clearValidators();
      this.loginForm.controls['auth_code'].updateValueAndValidity();
      this.showForm = 'userNameForm';
    }
    if (this.blackListedEmails?.includes(this.loginForm.value?.username)) {
      const controls = this.loginForm.controls['username'];
      controls.setErrors({
        ...controls.errors,
        alreadyExists: true,
      });
    }
  }
  signIn(url: String) {
    this.spinnerService.show();
    const loggedInDeviceID: string = this.deviceInfoService.getUniqueDeviceID();
    let requestData = {
      data: {
        ...this.loginForm.value,
        isPwa: window.matchMedia('(display-mode: standalone)').matches,
        unique_device_id: loggedInDeviceID,
      },
      url: url,
    };

    this.authService.verifyLogin(requestData).subscribe((response: any) => {
      if (response['status'] === 'success') {
        if (response?.data) {
          this.spinnerService.hide();
          this.emitData.emit({
            source: 'multipleSubscribers',
            response: response,
          });
        } else {
          this.emitData.emit({
            source: 'loginSuccessAction',
            response: response,
          });
        }
      } else {
        this.spinnerService.hide();
        this.emitData.emit({ source: 'displayMessage', response: response });
        // this.displayMessage = response['message'];
        if (response?.unique_device_id_failed) {
          // https://app.shortcut.com/sendem/story/10897/improve-security
          this.sendVCode('oneTime', true);
        }
      }
      this.checkAuthFailureInfo(response);
    });
  }
  login() {
    if (this.showForm === 'userNameForm') {
      //step 1: check whether the entered email address is valid
      this.checkUserName();
    }
    if (this.showForm === 'vCodeForm') {
      //step 2:if user is first time user check v-code sent to their mail
      this.signIn('register_login/login_with_code/');
    }
    if (
      this.showForm === 'passwordForm' ||
      this.showForm === 'dummyPasswordForm'
    ) {
      //step 3:if user has logged in at-least once or entered email is not in our db.
      //In either case check again username + password is in the db
      this.signIn('register_login/login/');
    }
  }
  checkUserName() {
    localStorage.removeItem('resetPasswordPWA');
    this.spinnerService.show();
    let requestData = {
      data: this.loginForm.value,

      url: 'register_login/verify_login/',
    };

    this.isPasswordAlreadyset = false;
    const manageSubscriptionRoutedLink: boolean =
      sessionStorage.getItem(btoa('manageSubscriptionRoutedLink')) == 'true';

    this.authService
      .verifyLogin(requestData)
      .subscribe(async (response: any) => {
        if (response['status'] === 'success') {
          localStorage.removeItem('email');
          this.checkAuthFailureInfo(response?.data);
          this.fidoRegistered = response['data']?.fido_credentials;

          this.isPasswordAlreadyset = response?.data?.password_set;
          if (manageSubscriptionRoutedLink) {
            this.sendVCode('oneTime');
          } else if (this.isPasswordAlreadyset) {
            this.showForm = 'passwordForm';
            this.shiftFocus('inputPassword');

            // case where user/ email has logged into the app at-least once
            this.loginForm.controls['auth_code'].removeValidators(
              Validators.required,
            );
            this.loginForm.controls['password'].setValidators(
              Validators.required,
            );
          } else {
            if (!this.hideSignInLInkForgotPasswordBtn) {
              this.sendVCode('oneTime');
              // first time user. avoid another btn click
            }
          }
        } else {
          // even if the user entered email address is not in our db;
          // we will show the user password field and sign in button;
          //from there he can call with fake data or can modify the details and try loggin in
          this.showForm = 'dummyPasswordForm';
          this.loginForm.controls['auth_code'].removeValidators(
            Validators.required,
          );
          this.loginForm.controls['password'].setValidators(
            Validators.required,
          );
          this.shiftFocus('inputPassword');
        }
        this.spinnerService.hide();
      });
  }
  async fidoStartAuth(responseBody: any) {
    try {
      let authResp = await startAuthentication({
        optionsJSON: JSON.parse(responseBody),
      });

      if (authResp) {
        this.spinnerService.show('Authenticating credentials');
        this.authService
          .fidoAuthenticate(
            {
              ...authResp,
              ...this.loginForm.value,
              isPwa: window.matchMedia('(display-mode: standalone)').matches,
            },
            { verify: 1 },
          )
          .subscribe((response: any) => {
            if (response['status'] == 'success') {
              if (response?.data) {
                sessionStorage.setItem('response', JSON.stringify(response));
                localStorage.setItem('email', this.loginForm.value.username);
                window.location.reload();
              } else {
                this.emitData.emit({
                  source: 'loginSuccessAction',
                  response: response,
                });
              }
            } else {
              sessionStorage.setItem('errorMessage', response['message']);
              localStorage.setItem('email', this.loginForm.value.username);
              window.location.reload();
            }
          });
      }
    } catch (err: any) {
      sessionStorage.setItem('errorMessage', String(err));
      localStorage.setItem('email', this.loginForm.value.username);
      window.location.reload();
    }
  }
  checkAuthFailureInfo(info: any) {
    this.hideSignInLInkForgotPasswordBtn = Boolean(
      info?.auth_code_failure_count > 4 &&
      new Date().getTime() - new Date(info?.last_modified_at).getTime() <
        10 * 60 * 1000,
    );
    if (
      this.hideSignInLInkForgotPasswordBtn &&
      this.showForm !== 'passwordForm'
    ) {
      // case where user enters wrong otp 5 times. switch them back to password form view
      this.showForm = 'passwordForm';
      this.shiftFocus('inputPassword');
      this.loginForm.controls['auth_code'].setValue('');
      this.loginForm.controls['password'].setValue('');

      this.loginForm.controls['auth_code'].clearValidators();
      this.loginForm.controls['auth_code'].updateValueAndValidity();
      this.loginForm.controls['password'].setValidators(Validators.required);
      this.loginForm.controls['password'].updateValueAndValidity();
      this.loginForm.updateValueAndValidity();
    }
  }

  authenticate() {
    this.spinnerService.show();
    var element = <HTMLInputElement>(
      document.getElementById('passwordInputField')
    );
    if (element) {
      element.hidden = true;
    }

    this.authService
      .fidoAuthenticate(this.loginForm.value)
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.fidoStartAuth(response['data']);
        } else {
          this.spinnerService.hide();
          if (response?.message)
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response?.message,
            });
          this.emitData.emit({ source: 'displayMessage', response: response });
        }
      });
  }
  shiftFocus(elementId: any) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);

      element?.focus();
    }, 300);
  }

  async sendVCode(source: string = 'forgotPassword', isNewDevice = false) {
    this.emitData.emit({ source: 'displayMessage', response: { message: '' } });

    let requestBody: any = this.loginForm.value;
    let params: any = {};
    if (localStorage.getItem('overRidePassword') === 'true') {
      // case where we will have to force create a password since the user didnt select and account
      params['override_code'] = 1;
      localStorage.removeItem('overRidePassword');
    }
    if (isNewDevice) {
      // get location and device info
      // https://app.shortcut.com/sendem/story/11252/change-message-for-otp-sent-from-new-device#activity-11674
      params.new_device = 1;
      requestBody.device_info =
        await this.deviceInfoService.getDeviceDetail('optional');
    }

    this.authService
      .sendVCode(requestBody, params)
      .subscribe((response: any) => {
        this.emitData.emit({ source: 'displayMessage', response: response });

        this.loginForm.controls['password'].clearValidators();
        this.loginForm.controls['password'].updateValueAndValidity();
        this.loginForm.controls['auth_code'].setValidators([
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
        ]);
        this.loginForm.controls['remember_login'].setValue(true);

        this.showForm = 'vCodeForm';
        this.shiftFocus('verCode');
        if (source === 'forgotPassword')
          window.localStorage.setItem('resetPasswordPWA', 'true');
      });
  }
}
